/**
 * The eight colours a unit may file its event types under.
 *
 * **Names, never values.** `pine`, not `#148156`. The hexes live in `tokens.css` in both theme
 * blocks and nowhere else, so a component never branches on theme and the palette can be retuned
 * without touching a row of data. This module is the picker's source of truth for which names
 * exist and in what order; the C# allow-list that validates the write boundary is the same eight,
 * and a swatch cannot exist in one and not the other — `EventColorPaletteTests` fails if it does.
 *
 * **A leader picks from these and cannot author arbitrary colour.** That is the point rather than a
 * limitation: a fixed set is what keeps another organisation's red out of our chrome, which is a
 * legal constraint (`handoff/README.md`) rather than a matter of taste. Three regions are excluded
 * — saturated orange and amber (announcements, unread, urgency), red (blocked and destructive), and
 * olive (compliance state, and the brand rule). See `tokens.css` for why Cocoa is allowed to sit
 * inside the amber band.
 *
 * **Colour is never the only signal.** The type's name renders as text beside it everywhere it
 * appears. Strip every colour from the running app and no screen becomes ambiguous — which is also
 * what makes eight hues safe for the roughly one in twelve men with a colour vision deficiency: two
 * of these will look alike to someone, and nothing depends on telling them apart.
 */
export const EVENT_COLORS = [
  'harbor',
  'teal',
  'pine',
  'plum',
  'cocoa',
  'violet',
  'berry',
  'graphite',
] as const;

export type EventColorKey = (typeof EVENT_COLORS)[number];

/** What a leader reads under the grid — "Pine selected". Capitalised names, not hue descriptions. */
export const EVENT_COLOR_LABELS: Record<EventColorKey, string> = {
  harbor: 'Harbor',
  teal: 'Teal',
  pine: 'Pine',
  plum: 'Plum',
  cocoa: 'Cocoa',
  violet: 'Violet',
  berry: 'Berry',
  graphite: 'Graphite',
};

/**
 * The neutral, and the answer for anything with no colour of its own — including an event filed
 * under no type at all, so a calendar never has a ragged row.
 */
export const NEUTRAL_EVENT_COLOR: EventColorKey = 'graphite';

export function isEventColor(key: string | null | undefined): key is EventColorKey {
  return !!key && (EVENT_COLORS as readonly string[]).includes(key);
}

/**
 * The CSS variable for a key, ready to drop into a `style` prop.
 *
 * An unknown key resolves to the neutral rather than to nothing: a swatch retired from the palette
 * must not break a calendar, and `var(--color-event-fuchsia)` would render as the inherited colour
 * of whatever it sat in — which on a left rule is invisible and on text is unreadable.
 */
export function eventColorVar(key: string | null | undefined): string {
  return `var(--color-event-${isEventColor(key) ? key : NEUTRAL_EVENT_COLOR})`;
}

/**
 * The colour a new **root** type should be offered: the first in palette order this unit has not
 * used, wrapping once they all have.
 *
 * Root types only, deliberately. A child type takes no colour of its own and renders its parent's,
 * which is what keeps every kind of campout reading as one thing on the calendar — handing a
 * `Backpacking Campout` the next swatch off the list would spread the palette and lose exactly
 * that. The leader can still override it to anything in the grid.
 *
 * The same rule runs server-side in `EventColors.NextAfter`, which is the one that actually
 * decides; this is here so the form can show what it is about to get.
 */
export function nextEventColor(alreadyUsed: readonly (string | null)[]): EventColorKey {
  const taken = alreadyUsed.filter(isEventColor);

  return (
    EVENT_COLORS.find((color) => !taken.includes(color)) ??
    EVENT_COLORS[taken.length % EVENT_COLORS.length]
  );
}
